import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import { filesApi } from '@/lib/api';

const PRESETS = [
  { mode: 'improve', icon: 'wand-magic-sparkles', label: 'Improve writing' },
  { mode: 'shorten', icon: 'compress', label: 'Make shorter' },
  { mode: 'expand', icon: 'expand', label: 'Make longer' },
  { mode: 'fix-grammar', icon: 'spell-check', label: 'Fix grammar' },
  { mode: 'simplify', icon: 'lightbulb', label: 'Simplify' },
  { mode: 'professional', icon: 'briefcase', label: 'More professional' },
  { mode: 'casual', icon: 'mug-hot', label: 'More casual' },
];

// Theme comes from the CSS variables set on `.gab-editor-root` (see
// editorChrome.web.jsx), so the popover matches the app's light/dark theme.
const V = {
  surface: 'var(--ed-surface, #ffffff)',
  raised: 'var(--ed-surface-raised, #f8fafc)',
  fg: 'var(--ed-fg, #111827)',
  muted: 'var(--ed-fg-muted, #64748b)',
  border: 'var(--ed-border, #e5e7eb)',
  accent: 'var(--ed-accent, #3157a4)',
  hover: 'var(--ed-hover, rgba(128, 128, 128, 0.14))',
};

/**
 * Floating "AI rewrite" panel used by the document and presentation editors.
 * Renders preset actions plus a custom instruction, previews the rewrite, and
 * hands the accepted text back through onApply.
 *
 * IMPORTANT: positioned with `absolute` (not `fixed`) relative to the editor
 * root. The editors live inside a sidebar whose ancestors carry transforms,
 * which re-anchors `fixed` elements and pushes them off screen. `position`
 * and `bounds` must therefore be editor-root-relative coordinates.
 */
export default function AiRewritePopover({
  selectionText,
  contextText = '',
  surface = 'document',
  position,
  bounds = null,
  onApply,
  onClose,
}) {
  // Icons can't consume the CSS variables in `V` (RN color processing), so
  // muted icon colors come straight from the theme.
  const { colors } = useTheme();
  const panelRef = useRef(null);
  const abortActiveRef = useRef(false);
  const [phase, setPhase] = useState('menu'); // menu | loading | preview | error
  const [customInstruction, setCustomInstruction] = useState('');
  const [activeLabel, setActiveLabel] = useState('');
  const [lastRequest, setLastRequest] = useState(null);
  const [rewritten, setRewritten] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Mount-scoped, so a parent re-render mid-request can't latch the abort flag
  // and strand the popover on "Rewriting your selection…" forever. Both callers
  // pass an inline onClose, so the listener effect below re-runs constantly.
  useEffect(() => () => { abortActiveRef.current = true; }, []);

  useEffect(() => {
    const onKeyDown = event => {
      if (event.key === 'Escape') onClose();
    };
    const onPointerDown = event => {
      if (panelRef.current && !panelRef.current.contains(event.target)) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [onClose]);

  const runRewrite = async request => {
    setPhase('loading');
    setActiveLabel(request.label);
    setLastRequest(request);
    abortActiveRef.current = false;
    try {
      const result = await filesApi.rewriteSelection({
        text: selectionText,
        mode: request.mode,
        instruction: request.instruction || '',
        surface,
        contextText,
      });
      if (abortActiveRef.current) return;
      setRewritten(result);
      setPhase('preview');
    } catch (error) {
      if (abortActiveRef.current) return;
      setErrorMessage(error?.message || 'Rewrite failed. Try again.');
      setPhase('error');
    }
  };

  const submitCustom = () => {
    const instruction = customInstruction.trim();
    if (instruction.length < 3) return;
    runRewrite({ mode: 'custom', instruction, label: 'Custom rewrite' });
  };

  const width = phase === 'preview' ? 380 : 264;
  const maxWidth = bounds?.width ? Math.max(220, bounds.width - 24) : width;
  const clampedWidth = Math.min(width, maxWidth);
  const maxTop = bounds?.height ? Math.max(8, bounds.height - 330) : position.top;
  const maxLeft = bounds?.width ? Math.max(8, bounds.width - clampedWidth - 12) : position.left;
  const style = {
    position: 'absolute',
    top: Math.max(8, Math.min(position.top, maxTop)),
    left: Math.max(8, Math.min(position.left, maxLeft)),
    width: clampedWidth,
    zIndex: 90,
    borderRadius: 11,
    background: V.surface,
    color: V.fg,
    border: `1px solid ${V.border}`,
    boxShadow: '0 10px 38px rgba(15, 23, 42, 0.28)',
    overflow: 'hidden',
    font: '13px/1.45 -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  };

  return (
    <div ref={panelRef} role="dialog" aria-label="AI rewrite" style={style}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 12px', borderBottom: `1px solid ${V.border}`, background: V.raised }}>
        <Icon name="wand-magic-sparkles" size={13} color="#3157a4" />
        <strong style={{ flex: 1, fontSize: 12.5, color: V.fg }}>
          {phase === 'preview' ? activeLabel : phase === 'loading' ? activeLabel : 'AI rewrite'}
        </strong>
        <button onClick={onClose} aria-label="Close AI rewrite" style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: 3, color: V.muted }}>
          <Icon name="xmark" size={13} color={colors.text_color_secondary} />
        </button>
      </div>

      {phase === 'menu' ? (
        <div style={{ padding: 6 }}>
          {PRESETS.map(preset => (
            <button
              key={preset.mode}
              onClick={() => runRewrite({ mode: preset.mode, label: preset.label })}
              style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '8px 10px', border: 0, borderRadius: 7, background: 'transparent', cursor: 'pointer', textAlign: 'left', fontSize: 13, color: V.fg }}
              onMouseEnter={event => { event.currentTarget.style.background = 'var(--ed-hover, rgba(128, 128, 128, 0.14))'; }}
              onMouseLeave={event => { event.currentTarget.style.background = 'transparent'; }}
            >
              <Icon name={preset.icon} size={13} color="#3157a4" />
              {preset.label}
            </button>
          ))}
          <div style={{ display: 'flex', gap: 6, padding: '7px 8px 5px', borderTop: `1px solid ${V.border}`, marginTop: 5 }}>
            <input
              value={customInstruction}
              onChange={event => setCustomInstruction(event.target.value)}
              onKeyDown={event => { if (event.key === 'Enter') submitCustom(); }}
              placeholder="Or tell the AI what to do…"
              maxLength={400}
              aria-label="Custom rewrite instruction"
              style={{ flex: 1, minWidth: 0, border: `1px solid ${V.border}`, borderRadius: 7, padding: '7px 9px', fontSize: 12.5, outline: 'none', background: V.surface, color: V.fg }}
            />
            <button
              onClick={submitCustom}
              disabled={customInstruction.trim().length < 3}
              aria-label="Run custom rewrite"
              style={{ border: 0, borderRadius: 7, width: 32, background: V.accent, color: '#fff', cursor: 'pointer', opacity: customInstruction.trim().length < 3 ? 0.45 : 1 }}
            >
              <Icon name="arrow-up" size={12} color="#fff" />
            </button>
          </div>
        </div>
      ) : null}

      {phase === 'loading' ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 14px', color: V.muted }}>
          <Icon name="spinner" size={15} color="#3157a4" />
          Rewriting your selection…
        </div>
      ) : null}

      {phase === 'preview' ? (
        <div>
          <div style={{ maxHeight: 220, overflowY: 'auto', padding: '12px 14px', whiteSpace: 'pre-wrap', fontSize: 13, color: V.fg, background: V.raised }}>
            {rewritten}
          </div>
          <div style={{ display: 'flex', gap: 7, padding: '10px 12px', borderTop: `1px solid ${V.border}` }}>
            <button
              onClick={() => onApply(rewritten)}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, border: 0, borderRadius: 7, padding: '8px 0', background: V.accent, color: '#fff', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}
            >
              <Icon name="check" size={12} color="#fff" />
              Replace
            </button>
            <button
              onClick={() => lastRequest && runRewrite(lastRequest)}
              title="Try again"
              aria-label="Try again"
              style={{ border: `1px solid ${V.border}`, borderRadius: 7, padding: '0 11px', background: V.surface, cursor: 'pointer', color: V.muted }}
            >
              <Icon name="rotate-right" size={12} color={colors.text_color_secondary} />
            </button>
            <button
              onClick={() => setPhase('menu')}
              title="Back to options"
              aria-label="Back to options"
              style={{ border: `1px solid ${V.border}`, borderRadius: 7, padding: '0 11px', background: V.surface, cursor: 'pointer', color: V.muted }}
            >
              <Icon name="arrow-left" size={12} color={colors.text_color_secondary} />
            </button>
          </div>
        </div>
      ) : null}

      {phase === 'error' ? (
        <div style={{ padding: '13px 14px' }}>
          <div role="alert" style={{ display: 'flex', gap: 8, color: '#b91c1c', fontSize: 12.5, marginBottom: 10 }}>
            <Icon name="triangle-exclamation" size={13} color="#b91c1c" />
            {errorMessage}
          </div>
          <button
            onClick={() => setPhase('menu')}
            style={{ width: '100%', border: `1px solid ${V.border}`, borderRadius: 7, padding: '8px 0', background: V.surface, cursor: 'pointer', fontWeight: 600, color: V.fg }}
          >
            Back to options
          </button>
        </div>
      ) : null}
    </div>
  );
}
